/**
 * RWBR — THE BOOK. Every number the league knows, updated the moment a race is
 * over rather than the moment a year is.
 *
 * Howard, 18 Aug 2026:
 *
 *   "it says that the stats are there at the end of the season but that's not
 *    what I want. I want those stats updated every week… crazy thorough,
 *    filterable, sortable. I want so many stats it's crazy — at driver level and
 *    team level. I want their team logos to be on it. I want the appropriate
 *    logos for the races… and then at the season end you can summarize years,
 *    you can put the years together to track year over year, but this has to be
 *    running week over week statistics."
 *
 * ===========================================================================
 * THE FOUR THINGS ON THIS SCREEN, AND WHY EACH ONE IS SEPARATE
 * ===========================================================================
 *
 * 1 · THE TILES — six facts a person wants before they want a table: who leads,
 *     who has won most, who has led most, who runs best, who has banked most,
 *     and who cannot buy a break. Every one is a link into the table below it.
 *
 * 2 · THE DRIVER TABLE — sixty-odd columns behind seven PRESETS, because
 *     sixty columns at once is a spreadsheet and nobody reads a spreadsheet.
 *     Sortable on every column, filterable by name, by shop, and by cut of the
 *     field. Sorting is over the FILTERED set, so "top ten by escape rate" means
 *     the ten in front of you, not ten somewhere else.
 *
 * 3 · THE SEASON GRID — one column per race across the whole calendar with the
 *     race's own crest above it, one row per driver, his finish in every cell.
 *     This is the only view on the screen that shows a SHAPE rather than a
 *     number: a bad August is a band of dark cells and you see it without
 *     reading anything.
 *
 * 4 · THE SHOPS — the same treatment for the garage, with each shop's mark
 *     beside its name and its cars listed underneath it.
 *
 * AND THE PICKER decides whether all of that means one season or every season.
 * On ALL, the grid stops being weeks and becomes years — one column per season,
 * the driver's championship finish in each — which is the year-over-year view,
 * built out of the same rows by the same fold.
 *
 * ===========================================================================
 * NOTHING HERE COMPUTES A RESULT
 * ===========================================================================
 *
 * Every figure comes off `statsModel.ts`, which reads `standings.ts`'s fold and
 * `money.ts`'s ledger and does not decide anything either. This file sorts,
 * filters, formats and paints. If a number on this screen is wrong, it is wrong
 * in the engine, and that is the only arrangement under which a stat board is
 * worth looking at.
 *
 * A LOGO THAT HAS NOT BEEN UPLOADED IS NOT A GREY BOX. Race crests render as
 * nothing at all (`RaceLogo`) and shops fall back to their own paint with a
 * two-letter monogram (`TeamMark`), because forty-three placeholder squares
 * down a column is a worse screen than none.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import RaceLogo from './RaceLogo.tsx';
import TeamMark from './TeamMark.tsx';
import { ordinal } from './boardModel.ts';
import { formatDollars } from '../domain/finance/money.ts';
import {
  boardFor,
  type DriverRaceLine,
  type RaceColumn,
  type SeasonPick,
  type StatBoard,
  type StatRow,
  type StatsSource,
  type TeamRow,
} from './statsModel.ts';
import { Cite } from './Cite.tsx';
import { useCiteText } from '../lib/prefs.ts';

// ---------------------------------------------------------------------------
// Formatting — the only opinions in this file
// ---------------------------------------------------------------------------

const dash = '—';
const num = (v: number | null | undefined): ReactNode => (v === null || v === undefined ? dash : v);
const one = (v: number | null | undefined): ReactNode =>
  v === null || v === undefined ? dash : v.toFixed(1);
const pctText = (v: number | null | undefined): ReactNode =>
  v === null || v === undefined ? dash : `${v.toFixed(1)}%`;
const money = (v: number | null | undefined): ReactNode =>
  v === null || v === undefined ? dash : formatDollars(v);
/**
 * ⚠ FOR THE ENGINE'S TWO FRACTIONS ONLY — `rafPct` and `escapePct`.
 *
 * `standings.ts` stores those as 0–1 (see the note on `pct` in `statsModel.ts`:
 * SAG/8 reads the escape figure back as a RATING, and a rating is not a
 * percentage). Everything else on this screen is already 0–100. Two units, two
 * formatters, and the wrong one is a hundredfold error that reads as a
 * rendering bug rather than as a wrong number.
 */
const fracPct = (v: number | null | undefined): ReactNode =>
  v === null || v === undefined ? dash : `${(v * 100).toFixed(1)}%`;
/** A zero is noise in a column of counts; a blank is not. */
const count = (v: number): ReactNode => (v === 0 ? '' : v);

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

interface Col<T> {
  key: string;
  /** The header. Short — these are stacked forty across. */
  label: string;
  /** What it means, in a sentence, on hover. Every column has one. */
  title: string;
  /** The sort value. Null sorts last in both directions. */
  value: (r: T) => number | string | null;
  render: (r: T) => ReactNode;
  /** Right-aligned, tabular figures. True for everything but names. */
  num?: boolean;
  /** First click sorts descending. True for "more is better" columns. */
  desc?: boolean;
  className?: (r: T) => string | undefined;
}

/** DNF causes, in the order `Car.outReason` can produce them. */
const OUT_REASONS: readonly { key: string; label: string; title: string }[] = [
  { key: 'trouble', label: 'Wrk', title: 'DNFs out of TROUBLE — wrecked, and did not get going again' },
  { key: 'problem', label: 'Prb', title: 'DNFs off the PROBLEM chart — the car let go' },
  { key: 'feud', label: 'Fud', title: 'DNFs out of a feud or a fume — somebody put him in the wall' },
  { key: 'fuel', label: 'Fue', title: 'Ran out of fuel — skipped the last stop and did not make it' },
  { key: 'black-flag', label: 'Blk', title: 'Black-flagged out of the race' },
  { key: 'repair-failed', label: 'Rpr', title: 'Went behind the wall and the repair failed' },
  { key: 'injury', label: 'Inj', title: 'Out of the car — hurt' },
];

function driverColumns(): Col<StatRow>[] {
  const cols: Col<StatRow>[] = [
    {
      key: 'rank',
      label: '#',
      title: 'Championship position',
      value: (r) => r.rank,
      render: (r) => r.rank,
      num: true,
    },
    {
      key: 'delta',
      label: 'Δ',
      title: 'Championship places gained or lost since the race before last',
      value: (r) => r.rankDelta ?? 0,
      desc: true,
      render: (r) =>
        r.rankDelta === null || r.rankDelta === 0 ? (
          <span className="sb__flat">–</span>
        ) : r.rankDelta > 0 ? (
          <span className="sb__up">▲{r.rankDelta}</span>
        ) : (
          <span className="sb__down">▼{Math.abs(r.rankDelta)}</span>
        ),
      num: true,
    },
    // ---- the championship -------------------------------------------------
    { key: 'points', label: 'Pts', title: 'Championship points: 43 down to 1, +3 for the win, +1 led, +1 led most (D-023)', value: (r) => r.points, render: (r) => r.points, num: true, desc: true },
    { key: 'behind', label: 'Back', title: 'Points behind the leader of this table', value: (r) => r.pointsBehind, render: (r) => (r.pointsBehind === 0 ? '' : `−${r.pointsBehind}`), num: true },
    { key: 'gap', label: 'Gap', title: 'Points behind the man one place ahead', value: (r) => r.pointsBehindNext, render: (r) => (r.pointsBehindNext === 0 ? '' : r.pointsBehindNext), num: true },
    { key: 'ppst', label: 'Pt/St', title: 'Average points per start — the tiebreak (D-012)', value: (r) => r.avgPointsPerStart, render: (r) => one(r.avgPointsPerStart), num: true, desc: true },
    { key: 'forfeit', label: 'Frft', title: 'Points forfeited to TV EVENT X 13 — he raced, he was paid, he scored nothing', value: (r) => r.pointsForfeited, render: (r) => count(r.pointsForfeited), num: true, desc: true, className: (r) => (r.pointsForfeited > 0 ? 'sb__bad' : undefined) },
    { key: 'championships', label: 'Cup', title: 'Championships won', value: (r) => r.championships, render: (r) => count(r.championships), num: true, desc: true, className: () => 'sb__gold' },

    // ---- the racing -------------------------------------------------------
    { key: 'wins', label: 'W', title: 'Race wins', value: (r) => r.wins, render: (r) => count(r.wins), num: true, desc: true, className: (r) => (r.wins > 0 ? 'sb__win' : undefined) },
    { key: 'poles', label: 'Pole', title: 'Poles — started first', value: (r) => r.poles, render: (r) => count(r.poles), num: true, desc: true },
    { key: 'top5', label: 'T5', title: 'Top-five finishes', value: (r) => r.top5, render: (r) => count(r.top5), num: true, desc: true },
    { key: 'top10', label: 'T10', title: 'Top-ten finishes', value: (r) => r.top10, render: (r) => count(r.top10), num: true, desc: true },
    { key: 'starts', label: 'St', title: 'Races started', value: (r) => r.starts, render: (r) => r.starts, num: true, desc: true },
    { key: 'entries', label: 'Ent', title: 'Races he turned up for, whether or not he made the show', value: (r) => r.entries, render: (r) => r.entries, num: true, desc: true },
    { key: 'dnqs', label: 'DNQ', title: 'Failed to qualify', value: (r) => r.dnqs, render: (r) => count(r.dnqs), num: true, desc: true, className: (r) => (r.dnqs > 0 ? 'sb__bad' : undefined) },
    { key: 'missed', label: 'Bub', title: 'Made the entry list and was left on the bubble (RWEG/p.4)', value: (r) => r.missedTheShow, render: (r) => count(r.missedTheShow), num: true, desc: true },
    { key: 'absent', label: 'Abs', title: 'Not at the track at all — suspended, injured, without a ride, or worse', value: (r) => r.racesMissed, render: (r) => count(r.racesMissed), num: true, desc: true },
    { key: 'finished', label: 'Fin', title: 'Races finished', value: (r) => r.racesFinished, render: (r) => r.racesFinished, num: true, desc: true },
    { key: 'dnfs', label: 'DNF', title: 'Did not finish', value: (r) => r.dnfs, render: (r) => count(r.dnfs), num: true, desc: true, className: (r) => (r.dnfs > 0 ? 'sb__bad' : undefined) },
    { key: 'raf', label: 'RAF%', title: 'Races finished ÷ races started (N-007). The winter reads this one.', value: (r) => r.rafPct, render: (r) => fracPct(r.rafPct), num: true, desc: true },
    { key: 'avgStart', label: 'AvSt', title: 'Average starting position. The winter assigns a Trophy off this (N-010).', value: (r) => r.avgStart, render: (r) => one(r.avgStart), num: true },
    { key: 'avgFinish', label: 'AvFin', title: 'Average finishing position', value: (r) => r.avgFinish, render: (r) => one(r.avgFinish), num: true },
    { key: 'best', label: 'Best', title: 'Best finish of the year', value: (r) => r.bestFinish, render: (r) => num(r.bestFinish), num: true },
    { key: 'worst', label: 'Wrst', title: 'Worst finish of the year', value: (r) => r.worstFinish, render: (r) => num(r.worstFinish), num: true, desc: true },
    { key: 'winPct', label: 'W%', title: 'Wins ÷ starts', value: (r) => r.winPct, render: (r) => pctText(r.winPct), num: true, desc: true },
    { key: 't5Pct', label: 'T5%', title: 'Top fives ÷ starts', value: (r) => r.top5Pct, render: (r) => pctText(r.top5Pct), num: true, desc: true },
    { key: 't10Pct', label: 'T10%', title: 'Top tens ÷ starts', value: (r) => r.top10Pct, render: (r) => pctText(r.top10Pct), num: true, desc: true },

    // ---- out front --------------------------------------------------------
    { key: 'turnsLed', label: 'Led', title: 'Turns led. A turn is 10 miles (p.ii), not a lap.', value: (r) => r.turnsLed, render: (r) => count(r.turnsLed), num: true, desc: true },
    { key: 'racesLed', label: 'RcLd', title: 'Races in which he led at least one turn — the +1 for leading (D-023)', value: (r) => r.racesLed, render: (r) => count(r.racesLed), num: true, desc: true },
    { key: 'mostLed', label: 'Most', title: 'Races in which he led the most turns — the second +1', value: (r) => r.mostTurnsLedRaces, render: (r) => count(r.mostTurnsLedRaces), num: true, desc: true },
    { key: 'ledPer', label: 'Ld/St', title: 'Turns led per start', value: (r) => r.turnsLedPerStart, render: (r) => one(r.turnsLedPerStart), num: true, desc: true },
    { key: 'ledPct', label: 'Ld%', title: 'Races led ÷ starts', value: (r) => r.ledPct, render: (r) => pctText(r.ledPct), num: true, desc: true },

    // ---- trouble ----------------------------------------------------------
    { key: 'trouble', label: 'Trb', title: 'TROUBLE rolls faced', value: (r) => r.troubleAttempts, render: (r) => count(r.troubleAttempts), num: true, desc: true },
    { key: 'escapes', label: 'Esc', title: 'TROUBLE escaped on the dice', value: (r) => r.troubleEscapes, render: (r) => count(r.troubleEscapes), num: true, desc: true },
    { key: 'escPct', label: 'Esc%', title: "Escapes ÷ attempts. Next season's ESCAPE rating IS this number (SAG/8).", value: (r) => r.escapePct, render: (r) => fracPct(r.escapePct), num: true, desc: true },
    { key: 'chipSaves', label: 'Chip', title: 'Bought out of trouble with a performance chip — not an escape', value: (r) => r.troubleChipSaves, render: (r) => count(r.troubleChipSaves), num: true, desc: true },
    { key: 'collected', label: 'Coll', title: 'Collected in somebody else’s trouble', value: (r) => r.troubleCollected, render: (r) => count(r.troubleCollected), num: true, desc: true },
    ...OUT_REASONS.map(
      (o): Col<StatRow> => ({
        key: `out-${o.key}`,
        label: o.label,
        title: o.title,
        value: (r) => r.dnfsByCause[o.key] ?? 0,
        render: (r) => count(r.dnfsByCause[o.key] ?? 0),
        num: true,
        desc: true,
      }),
    ),

    // ---- heat -------------------------------------------------------------
    { key: 'duels', label: 'Duel', title: 'Duels he started', value: (r) => r.duelsInstigated, render: (r) => count(r.duelsInstigated), num: true, desc: true },
    { key: 'duelsWon', label: 'DuW', title: 'Duels won', value: (r) => r.duelsWon, render: (r) => count(r.duelsWon), num: true, desc: true },
    { key: 'duelsLost', label: 'DuL', title: 'Duels lost', value: (r) => r.duelsLost, render: (r) => count(r.duelsLost), num: true, desc: true },
    { key: 'duelPct', label: 'Du%', title: 'Duels won ÷ duels settled', value: (r) => r.duelWinPct, render: (r) => pctText(r.duelWinPct), num: true, desc: true },
    { key: 'heat', label: 'Heat', title: 'Duels, blow-ups and race-week altercations. The top 25% of this list are dealt HEAT in the winter (SAG/7).', value: (r) => r.heatIncidents, render: (r) => count(r.heatIncidents), num: true, desc: true, className: (r) => (r.heatIncidents > 0 ? 'sb__hot' : undefined) },
    { key: 'feuds', label: 'Feud', title: 'Races he went into with somebody', value: (r) => r.feudRaces, render: (r) => count(r.feudRaces), num: true, desc: true },

    // ---- form -------------------------------------------------------------
    {
      key: 'last5',
      label: 'Last 5',
      title: 'His last five finishes, oldest on the left',
      value: (r) => (r.lastFive.length ? r.lastFive.reduce((a, b) => a + b, 0) / r.lastFive.length : null),
      render: (r) =>
        r.lastFive.length === 0 ? (
          <span className="dim">{dash}</span>
        ) : (
          <span className="sb__form">
            {r.lastFive.map((f, i) => (
              <i key={i} className={`sb__pip ${pipClass(f)}`}>
                {f}
              </i>
            ))}
          </span>
        ),
    },
    { key: 'streak', label: 'T10 str', title: 'Consecutive races finished in the top ten, counting back from his last one', value: (r) => r.top10Streak, render: (r) => count(r.top10Streak), num: true, desc: true },
    { key: 'startStreak', label: 'St str', title: 'Consecutive races started — broken by a DNQ or a week off', value: (r) => r.startStreak, render: (r) => count(r.startStreak), num: true, desc: true },
    { key: 'lastWin', label: 'Last W', title: 'The race number of his most recent win', value: (r) => r.lastWinRace, render: (r) => (r.lastWinRace ? `R${r.lastWinRace}` : dash), num: true, desc: true },

    // ---- money ------------------------------------------------------------
    { key: 'winnings', label: 'Won', title: 'Purse and contingency — money won racing, not money paid to show up', value: (r) => r.winningsMb, render: (r) => money(r.winningsMb), num: true, desc: true, className: () => 'sb__money' },
    { key: 'perStart', label: '$/St', title: 'Winnings per start', value: (r) => r.winningsPerStartMb, render: (r) => money(r.winningsPerStartMb), num: true, desc: true, className: () => 'sb__money' },
    { key: 'income', label: 'In', title: 'Everything that came in — purse, contingency, sponsorship, salary, the lot', value: (r) => r.incomeMb, render: (r) => money(r.incomeMb), num: true, desc: true },
    { key: 'expense', label: 'Out', title: 'Everything that went out', value: (r) => r.expenseMb, render: (r) => money(r.expenseMb), num: true, desc: true },
    { key: 'balance', label: 'P&L', title: 'What is left', value: (r) => r.balanceMb, render: (r) => money(r.balanceMb), num: true, desc: true, className: (r) => (r.balanceMb < 0 ? 'sb__bad' : 'sb__money') },
    { key: 'seasons', label: 'Yrs', title: 'Seasons raced', value: (r) => r.seasons, render: (r) => r.seasons, num: true, desc: true },
  ];
  return cols;
}

const DRIVER_COLUMNS = driverColumns();
const DRIVER_BY_KEY = new Map(DRIVER_COLUMNS.map((c) => [c.key, c]));

/**
 * THE PRESETS — because sixty columns at once is a spreadsheet.
 *
 * Each is a reading of the same rows: what the championship looks like, what
 * the racing looked like, how hard the car got hit, how hard the driver hit
 * back, how he is going right now, and what any of it paid.
 */
const PRESETS: readonly { key: string; label: string; note: string; cols: string[] }[] = [
  {
    key: 'overview',
    label: 'Overview',
    note: 'The championship at a glance',
    cols: ['rank', 'delta', 'points', 'behind', 'starts', 'wins', 'top5', 'top10', 'turnsLed', 'avgStart', 'avgFinish', 'dnfs', 'winnings'],
  },
  {
    key: 'championship',
    label: 'Championship',
    note: 'Points, and everything that makes them',
    cols: ['rank', 'delta', 'points', 'behind', 'gap', 'ppst', 'wins', 'poles', 'top5', 'top10', 'racesLed', 'mostLed', 'forfeit', 'championships'],
  },
  {
    key: 'racing',
    label: 'The racing',
    note: 'Where he started, where he ended up, how often he got there',
    cols: ['rank', 'entries', 'starts', 'dnqs', 'missed', 'absent', 'finished', 'dnfs', 'raf', 'avgStart', 'avgFinish', 'best', 'worst', 'winPct', 't5Pct', 't10Pct'],
  },
  {
    key: 'front',
    label: 'Out front',
    note: 'Time spent leading — ten miles a turn',
    cols: ['rank', 'wins', 'poles', 'turnsLed', 'racesLed', 'mostLed', 'ledPer', 'ledPct', 'lastWin'],
  },
  {
    key: 'trouble',
    label: 'Trouble',
    note: 'What broke, what hit him, and what he got out of',
    cols: ['rank', 'trouble', 'escapes', 'escPct', 'chipSaves', 'collected', 'dnfs', ...OUT_REASONS.map((o) => `out-${o.key}`)],
  },
  {
    key: 'heat',
    label: 'Heat',
    note: 'The aggression the winter reads (SAG/7)',
    cols: ['rank', 'duels', 'duelsWon', 'duelsLost', 'duelPct', 'heat', 'feuds', 'collected'],
  },
  {
    key: 'form',
    label: 'Form',
    note: 'How he is going right now, not how the year went',
    cols: ['rank', 'delta', 'points', 'last5', 'streak', 'startStreak', 'lastWin', 'avgFinish'],
  },
  {
    key: 'money',
    label: 'The money',
    note: 'Purse, contingency and the books behind them',
    cols: ['rank', 'starts', 'wins', 'winnings', 'perStart', 'income', 'expense', 'balance'],
  },
  {
    key: 'everything',
    label: 'Everything',
    note: 'All of it. Scroll sideways.',
    cols: DRIVER_COLUMNS.map((c) => c.key).filter((k) => k !== 'last5'),
  },
];

// ---------------------------------------------------------------------------
// Team columns
// ---------------------------------------------------------------------------

const TEAM_COLUMNS: Col<TeamRow>[] = [
  { key: 'rank', label: '#', title: 'Shop position on points', value: (t) => t.rank, render: (t) => t.rank, num: true },
  {
    key: 'delta',
    label: 'Δ',
    title: 'Places gained or lost since the race before last',
    value: (t) => t.rankDelta ?? 0,
    desc: true,
    num: true,
    render: (t) =>
      t.rankDelta === null || t.rankDelta === 0 ? (
        <span className="sb__flat">–</span>
      ) : t.rankDelta > 0 ? (
        <span className="sb__up">▲{t.rankDelta}</span>
      ) : (
        <span className="sb__down">▼{Math.abs(t.rankDelta)}</span>
      ),
  },
  { key: 'points', label: 'Pts', title: 'Points scored by every car the shop ran', value: (t) => t.points, render: (t) => t.points, num: true, desc: true },
  { key: 'cars', label: 'Cars', title: 'Drivers on the charter', value: (t) => t.driverIds.length, render: (t) => t.driverIds.length, num: true, desc: true },
  { key: 'starts', label: 'St', title: 'Starts by all its cars', value: (t) => t.starts, render: (t) => t.starts, num: true, desc: true },
  { key: 'wins', label: 'W', title: 'Race wins', value: (t) => t.wins, render: (t) => count(t.wins), num: true, desc: true, className: (t) => (t.wins > 0 ? 'sb__win' : undefined) },
  { key: 'poles', label: 'Pole', title: 'Poles', value: (t) => t.poles, render: (t) => count(t.poles), num: true, desc: true },
  { key: 'top5', label: 'T5', title: 'Top fives', value: (t) => t.top5, render: (t) => count(t.top5), num: true, desc: true },
  { key: 'top10', label: 'T10', title: 'Top tens', value: (t) => t.top10, render: (t) => count(t.top10), num: true, desc: true },
  { key: 'turnsLed', label: 'Led', title: 'Turns led', value: (t) => t.turnsLed, render: (t) => count(t.turnsLed), num: true, desc: true },
  { key: 'racesLed', label: 'RcLd', title: 'Races led', value: (t) => t.racesLed, render: (t) => count(t.racesLed), num: true, desc: true },
  { key: 'dnqs', label: 'DNQ', title: 'Failures to qualify', value: (t) => t.dnqs, render: (t) => count(t.dnqs), num: true, desc: true },
  { key: 'dnfs', label: 'DNF', title: 'Cars that did not finish', value: (t) => t.dnfs, render: (t) => count(t.dnfs), num: true, desc: true, className: (t) => (t.dnfs > 0 ? 'sb__bad' : undefined) },
  { key: 'avgStart', label: 'AvSt', title: 'Average start across the stable, weighted by starts', value: (t) => t.avgStart, render: (t) => one(t.avgStart), num: true },
  { key: 'avgFinish', label: 'AvFin', title: 'Average finish across the stable, weighted by starts', value: (t) => t.avgFinish, render: (t) => one(t.avgFinish), num: true },
  { key: 'best', label: 'Best', title: 'Best finish by any of its cars', value: (t) => t.bestFinish, render: (t) => num(t.bestFinish), num: true },
  { key: 'winPct', label: 'W%', title: 'Wins ÷ starts', value: (t) => t.winPct, render: (t) => pctText(t.winPct), num: true, desc: true },
  { key: 'finPct', label: 'Fin%', title: 'Races finished ÷ starts', value: (t) => t.finishPct, render: (t) => pctText(t.finishPct), num: true, desc: true },
  { key: 'escPct', label: 'Esc%', title: 'TROUBLE escaped ÷ TROUBLE faced', value: (t) => t.escapePct, render: (t) => pctText(t.escapePct), num: true, desc: true },
  { key: 'heat', label: 'Heat', title: 'Duels, blow-ups and altercations across the stable', value: (t) => t.heatIncidents, render: (t) => count(t.heatIncidents), num: true, desc: true },
  { key: 'ppst', label: 'Pt/St', title: 'Points per start', value: (t) => t.pointsPerStart, render: (t) => one(t.pointsPerStart), num: true, desc: true },
  { key: 'winnings', label: 'Won', title: 'Purse and contingency won by all its cars', value: (t) => t.winningsMb, render: (t) => money(t.winningsMb), num: true, desc: true, className: () => 'sb__money' },
  { key: 'seasons', label: 'Yrs', title: 'Seasons on the charter', value: (t) => t.seasons, render: (t) => t.seasons, num: true, desc: true },
];

// ---------------------------------------------------------------------------
// Sorting and filtering
// ---------------------------------------------------------------------------

type Dir = 'asc' | 'desc';

function sortRows<T>(rows: readonly T[], col: Col<T> | undefined, dir: Dir): T[] {
  if (!col) return [...rows];
  const out = [...rows];
  out.sort((a, b) => {
    const av = col.value(a);
    const bv = col.value(b);
    // Null is "no answer yet", not "worst" — it goes to the bottom whichever
    // way the column is pointing, because a driver with no starts is not the
    // slowest man in the field.
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    const cmp = typeof av === 'string' || typeof bv === 'string'
      ? String(av).localeCompare(String(bv))
      : av - bv;
    return dir === 'asc' ? cmp : -cmp;
  });
  return out;
}

type Cut = 'all' | 'top10' | 'playoff' | 'winners' | 'started' | 'parked';

const CUTS: readonly { key: Cut; label: string; title: string }[] = [
  { key: 'all', label: 'Everyone', title: 'Every licensed driver' },
  { key: 'top10', label: 'Top 10', title: 'The top ten in the championship' },
  { key: 'playoff', label: 'Playoff 16', title: 'The sixteen the playoff is seeded from (D-012)' },
  { key: 'winners', label: 'Winners', title: 'Anyone who has won a race' },
  { key: 'started', label: 'Made a race', title: 'Anyone who has started at least one' },
  { key: 'parked', label: 'Never started', title: 'DNQs, bubble men and anyone who has not raced' },
];

function applyCut(rows: readonly StatRow[], cut: Cut): StatRow[] {
  switch (cut) {
    case 'top10':
      return rows.filter((r) => r.rank <= 10);
    case 'playoff':
      return rows.filter((r) => r.rank <= 16);
    case 'winners':
      return rows.filter((r) => r.wins > 0);
    case 'started':
      return rows.filter((r) => r.starts > 0);
    case 'parked':
      return rows.filter((r) => r.starts === 0);
    default:
      return [...rows];
  }
}

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------

export interface StatsBoardProps {
  sources: readonly StatsSource[];
  leagueName: string;
  /** Open a driver's dossier. */
  onDriver: (driverId: string) => void;
  /** Back to the racing, when there is a season to go back to. */
  onPlay?: () => void;
  /** Where the live season has got to, for the header line. */
  liveThrough?: { seasonNumber: number; racesRun: number } | null;
}

type Level = 'drivers' | 'teams';

export default function StatsBoard({ sources, leagueName, onDriver, onPlay, liveThrough }: StatsBoardProps) {
  const ct = useCiteText();
  const latest = sources[sources.length - 1]?.seasonNumber ?? 1;
  const [pick, setPick] = useState<SeasonPick>(latest);
  const [level, setLevel] = useState<Level>('drivers');
  const [presetKey, setPresetKey] = useState('overview');
  const [sortKey, setSortKey] = useState('rank');
  const [dir, setDir] = useState<Dir>('asc');
  const [query, setQuery] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [cut, setCut] = useState<Cut>('all');
  const [gridOpen, setGridOpen] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const board = useMemo(() => boardFor(sources, pick), [sources, pick]);
  const preset = PRESETS.find((p) => p.key === presetKey) ?? PRESETS[0];

  const columns = useMemo(
    () => preset.cols.map((k) => DRIVER_BY_KEY.get(k)).filter((c): c is Col<StatRow> => Boolean(c)),
    [preset],
  );

  const onSort = useCallback(
    (key: string, prefersDesc: boolean) => {
      setSortKey((was) => {
        if (was === key) {
          setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          return was;
        }
        setDir(prefersDesc ? 'desc' : 'asc');
        return key;
      });
    },
    [],
  );

  const drivers = useMemo(() => {
    if (!board) return [];
    const q = query.trim().toLowerCase();
    let rows = applyCut(board.drivers, cut);
    if (teamFilter) rows = rows.filter((r) => r.teamName === teamFilter);
    if (q) {
      rows = rows.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.teamName.toLowerCase().includes(q) ||
          r.carNumber.toLowerCase() === q,
      );
    }
    const col = level === 'drivers' ? DRIVER_BY_KEY.get(sortKey) : undefined;
    return sortRows(rows, col, dir);
  }, [board, cut, teamFilter, query, sortKey, dir, level]);

  const teams = useMemo(() => {
    if (!board) return [];
    const q = query.trim().toLowerCase();
    let rows = board.teams;
    if (teamFilter) rows = rows.filter((t) => t.name === teamFilter);
    if (q) rows = rows.filter((t) => t.name.toLowerCase().includes(q));
    const col = TEAM_COLUMNS.find((c) => c.key === sortKey);
    return sortRows(rows, col ?? TEAM_COLUMNS[0], col ? dir : 'asc');
  }, [board, teamFilter, query, sortKey, dir]);

  if (!board) {
    return (
      <div className="sb sb--empty">
        <h2>Nothing has been raced yet</h2>
        <p className="dim">
          Start a season from Home and this fills in the moment the first checkered flag
          falls — and again after every race after it.
        </p>
        {onPlay && (
          <button type="button" className="btn btn--hot" onClick={onPlay}>
            Go racing
          </button>
        )}
      </div>
    );
  }

  const teamNames = [...new Set(board.teams.map((t) => t.name))].sort();
  const live = liveThrough && pick === liveThrough.seasonNumber;

  return (
    <div className="sb">
      {/* ---- the picker ---------------------------------------------------- */}
      <header className="sb__top">
        <div className="sb__who">
          <div className="sb__kicker">{leagueName} · the book</div>
          <h2>
            {board.label}
            {board.seasonNumber !== null && (
              <span className="sb__through">
                {board.throughRace === 0
                  ? 'no races run'
                  : `through race ${board.throughRace} of ${board.totalRaces}`}
              </span>
            )}
            {board.seasonNumber === null && (
              <span className="sb__through">
                {board.seasonNumbers.length} season{board.seasonNumbers.length === 1 ? '' : 's'} ·{' '}
                {board.throughRace} races
              </span>
            )}
          </h2>
          <p className="sb__sub">
            {live ? (
              <>
                <b className="sb__livedot">LIVE</b> Every number below is brought up to date after
                each race. Nothing waits for the winter.
              </>
            ) : board.complete ? (
              'Settled — raced, paid and re-rated.'
            ) : (
              'A season in progress.'
            )}
          </p>
        </div>

        <nav className="sb__seasons" aria-label="Season">
          {sources.map((s) => (
            <button
              key={s.seasonNumber}
              type="button"
              className={`sb__chip${pick === s.seasonNumber ? ' is-on' : ''}`}
              onClick={() => setPick(s.seasonNumber)}
              aria-pressed={pick === s.seasonNumber}
              title={`Season ${s.seasonNumber} · seed ${s.seed}`}
            >
              S{s.seasonNumber}
              {!s.complete && <i className="sb__chipdot" />}
            </button>
          ))}
          {sources.length > 1 && (
            <button
              type="button"
              className={`sb__chip sb__chip--all${pick === 'all' ? ' is-on' : ''}`}
              onClick={() => setPick('all')}
              aria-pressed={pick === 'all'}
              title="Every season added up — career totals, year over year"
            >
              All time
            </button>
          )}
        </nav>
      </header>

      {board.throughRace === 0 ? (
        <p className="sb__nothing">
          The calendar is set and the garage is chartered. The numbers arrive with the first
          checkered flag.
        </p>
      ) : (
        <Tiles board={board} onDriver={onDriver} />
      )}

      {/* ---- the controls -------------------------------------------------- */}
      <div className="sb__controls">
        <div className="sb__seg" role="tablist" aria-label="Level">
          {(['drivers', 'teams'] as Level[]).map((l) => (
            <button
              key={l}
              type="button"
              role="tab"
              aria-selected={level === l}
              className={`seg${level === l ? ' is-on' : ''}`}
              onClick={() => {
                setLevel(l);
                setSortKey('rank');
                setDir('asc');
              }}
            >
              {l === 'drivers' ? 'Drivers' : 'The shops'}
            </button>
          ))}
        </div>

        <input
          className="sb__search"
          type="search"
          placeholder="Find a driver, a shop, a car number…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search"
        />

        <select
          className="sb__select"
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          aria-label="Filter by shop"
        >
          <option value="">Every shop</option>
          {teamNames.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>

        {level === 'drivers' && (
          <select
            className="sb__select"
            value={cut}
            onChange={(e) => setCut(e.target.value as Cut)}
            aria-label="Filter the field"
          >
            {CUTS.map((c) => (
              <option key={c.key} value={c.key} title={ct(c.title)}>
                {c.label}
              </option>
            ))}
          </select>
        )}

        <span className="sb__count">
          {level === 'drivers' ? drivers.length : teams.length}{' '}
          {level === 'drivers' ? 'drivers' : 'shops'}
        </span>

        {(query || teamFilter || cut !== 'all') && (
          <button
            type="button"
            className="btn btn--quiet btn--tiny"
            onClick={() => {
              setQuery('');
              setTeamFilter('');
              setCut('all');
            }}
          >
            Clear
          </button>
        )}
      </div>

      {level === 'drivers' && (
        <>
          <div className="sb__presets" role="group" aria-label="Columns">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`sb__chip${presetKey === p.key ? ' is-on' : ''}`}
                onClick={() => setPresetKey(p.key)}
                aria-pressed={presetKey === p.key}
                title={ct(p.note)}
              >
                {p.label}
              </button>
            ))}
            <span className="sb__presetnote">{ct(preset.note)}</span>
          </div>

          <DriverTable
            rows={drivers}
            columns={columns}
            sortKey={sortKey}
            dir={dir}
            onSort={onSort}
            onDriver={onDriver}
            championId={board.championId}
          />
        </>
      )}

      {level === 'teams' && (
        <TeamTable
          rows={teams}
          sortKey={sortKey}
          dir={dir}
          onSort={onSort}
          onDriver={onDriver}
          expanded={expanded}
          onExpand={setExpanded}
        />
      )}

      {/* ---- the grid ------------------------------------------------------ */}
      <section className="sb__gridwrap">
        <button
          type="button"
          className="sb__gridtoggle"
          onClick={() => setGridOpen((v) => !v)}
          aria-expanded={gridOpen}
        >
          {gridOpen ? '▾' : '▸'}{' '}
          {board.seasonNumber === null ? 'Year by year' : 'Race by race'}
          <span className="dim">
            {board.seasonNumber === null
              ? ' — where every man finished the championship, season by season'
              : ' — where every man finished, week by week'}
          </span>
        </button>

        {gridOpen &&
          (board.seasonNumber === null ? (
            <YearGrid board={board} rows={drivers} onDriver={onDriver} />
          ) : (
            <RaceGrid board={board} rows={drivers} onDriver={onDriver} />
          ))}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The tiles
// ---------------------------------------------------------------------------

function Tiles({ board, onDriver }: { board: StatBoard; onDriver: (id: string) => void }) {
  const started = board.drivers.filter((d) => d.starts > 0);
  const best = <T,>(rows: readonly T[], pick: (r: T) => number | null): T | null => {
    let out: T | null = null;
    let high = -Infinity;
    for (const r of rows) {
      const v = pick(r);
      if (v === null) continue;
      if (v > high) {
        high = v;
        out = r;
      }
    }
    return out;
  };

  const leader = board.drivers[0] ?? null;
  const mostWins = best(started, (d) => d.wins);
  const mostLed = best(started, (d) => d.turnsLed);
  const bestAvg = best(
    started.filter((d) => d.starts >= Math.max(1, Math.floor(board.throughRace / 2))),
    (d) => (d.avgFinish === null ? null : -d.avgFinish),
  );
  const richest = best(started, (d) => d.winningsMb);
  const hardLuck = best(started, (d) => d.dnfs);

  const tiles: { label: string; row: StatRow | null; value: string; note: string }[] = [
    {
      label: board.seasonNumber === null ? 'Most points, all time' : 'Points leader',
      row: leader,
      value: leader ? `${leader.points}` : dash,
      note: leader ? `${leader.wins} win${leader.wins === 1 ? '' : 's'} · ${leader.teamName}` : '',
    },
    {
      label: 'Most wins',
      row: mostWins,
      value: mostWins ? `${mostWins.wins}` : dash,
      note: mostWins ? `from ${mostWins.starts} start${mostWins.starts === 1 ? '' : 's'}` : '',
    },
    {
      label: 'Most turns led',
      row: mostLed,
      value: mostLed ? mostLed.turnsLed.toLocaleString('en-US') : dash,
      note: mostLed ? `${(mostLed.turnsLed * 10).toLocaleString('en-US')} miles out front` : '',
    },
    {
      label: 'Best average finish',
      row: bestAvg,
      value: bestAvg ? (bestAvg.avgFinish as number).toFixed(1) : dash,
      note: bestAvg ? `${bestAvg.starts} starts · ${pctTextPlain(bestAvg.finishPct)} finished` : '',
    },
    {
      label: 'Most money won',
      row: richest,
      value: richest ? formatDollars(richest.winningsMb) : dash,
      note: richest ? `${formatDollars(richest.winningsPerStartMb ?? 0)} a start` : '',
    },
    {
      label: 'Hard luck',
      row: hardLuck,
      value: hardLuck ? `${hardLuck.dnfs}` : dash,
      note: hardLuck ? `DNFs · ${pctTextPlain(hardLuck.finishPct)} finished` : '',
    },
  ];

  return (
    <div className="sb__tiles">
      {tiles.map((t) => (
        <div className="sb__tile" key={t.label}>
          <div className="sb__tilelabel">{t.label}</div>
          <div className="sb__tileval">{t.value}</div>
          {t.row ? (
            <button type="button" className="sb__tilename linkish" onClick={() => onDriver((t.row as StatRow).driverId)}>
              <TeamMark teamName={t.row.teamName} size={15} />
              {t.row.name}
            </button>
          ) : (
            <div className="sb__tilename dim">nobody yet</div>
          )}
          <div className="sb__tilenote">{t.note}</div>
        </div>
      ))}
    </div>
  );
}

const pctTextPlain = (v: number | null): string => (v === null ? dash : `${v.toFixed(0)}%`);

// ---------------------------------------------------------------------------
// The driver table
// ---------------------------------------------------------------------------

function DriverTable({
  rows,
  columns,
  sortKey,
  dir,
  onSort,
  onDriver,
  championId,
}: {
  rows: readonly StatRow[];
  columns: readonly Col<StatRow>[];
  sortKey: string;
  dir: Dir;
  onSort: (key: string, desc: boolean) => void;
  onDriver: (id: string) => void;
  championId: string | null;
}) {
  const ct = useCiteText();
  if (rows.length === 0) {
    return <p className="sb__nothing">Nobody matches that. Clear the filters to see the field.</p>;
  }
  return (
    <div className="sb__tablewrap">
      <table className="sb__table">
        <thead>
          <tr>
            <th className="sb__sticky sb__namecol" scope="col">
              Driver
            </th>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={`${c.num ? 'num' : ''}${sortKey === c.key ? ' is-sorted' : ''}`}
                title={ct(c.title)}
              >
                <button type="button" className="sb__sort" onClick={() => onSort(c.key, Boolean(c.desc))}>
                  {c.label}
                  {sortKey === c.key && <i className="sb__arrow">{dir === 'asc' ? '▲' : '▼'}</i>}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.driverId} className={r.driverId === championId ? 'is-champ' : undefined}>
              <th scope="row" className="sb__sticky sb__namecol">
                <span className="sb__no">{r.carNumber}</span>
                <TeamMark teamName={r.teamName} size={18} />
                <button type="button" className="linkish nm" onClick={() => onDriver(r.driverId)}>
                  {r.name}
                </button>
                {r.driverId === championId && (
                  <span className="crown" title="Cup champion">
                    ★
                  </span>
                )}
                <span className="sb__shop">{r.teamName}</span>
              </th>
              {columns.map((c) => (
                <td key={c.key} className={`${c.num ? 'num' : ''} ${c.className?.(r) ?? ''}`}>
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The team table
// ---------------------------------------------------------------------------

function TeamTable({
  rows,
  sortKey,
  dir,
  onSort,
  onDriver,
  expanded,
  onExpand,
}: {
  rows: readonly TeamRow[];
  sortKey: string;
  dir: Dir;
  onSort: (key: string, desc: boolean) => void;
  onDriver: (id: string) => void;
  expanded: string | null;
  onExpand: (id: string | null) => void;
}) {
  const ct = useCiteText();
  if (rows.length === 0) {
    return <p className="sb__nothing">No shop matches that.</p>;
  }
  return (
    <div className="sb__tablewrap">
      <table className="sb__table">
        <thead>
          <tr>
            <th className="sb__sticky sb__namecol" scope="col">
              Shop
            </th>
            {TEAM_COLUMNS.slice(1).map((c) => (
              <th
                key={c.key}
                scope="col"
                className={`${c.num ? 'num' : ''}${sortKey === c.key ? ' is-sorted' : ''}`}
                title={ct(c.title)}
              >
                <button type="button" className="sb__sort" onClick={() => onSort(c.key, Boolean(c.desc))}>
                  {c.label}
                  {sortKey === c.key && <i className="sb__arrow">{dir === 'asc' ? '▲' : '▼'}</i>}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <ShopRows
              key={t.teamId}
              team={t}
              open={expanded === t.teamId}
              onToggle={() => onExpand(expanded === t.teamId ? null : t.teamId)}
              onDriver={onDriver}
            />
          ))}
        </tbody>
      </table>
      <p className="dim small">
        Click a shop to see its cars. A shop keeps its row in a week none of them started — a garage
        that vanishes when its cars are parked is not a garage.
      </p>
    </div>
  );
}

function ShopRows({
  team,
  open,
  onToggle,
  onDriver,
}: {
  team: TeamRow;
  open: boolean;
  onToggle: () => void;
  onDriver: (id: string) => void;
}) {
  return (
    <>
      <tr className={open ? 'is-open' : undefined}>
        <th scope="row" className="sb__sticky sb__namecol">
          <span className="sb__rk">{team.rank}</span>
          <TeamMark teamName={team.name} color={team.baseColor} size={20} />
          <button type="button" className="linkish nm" onClick={onToggle} aria-expanded={open}>
            {team.name}
          </button>
          <span className="sb__shop">
            {team.driverIds.length} car{team.driverIds.length === 1 ? '' : 's'}
          </span>
        </th>
        {TEAM_COLUMNS.slice(1).map((c) => (
          <td key={c.key} className={`${c.num ? 'num' : ''} ${c.className?.(team) ?? ''}`}>
            {c.render(team)}
          </td>
        ))}
      </tr>
      {open &&
        team.drivers.map((d) => (
          <tr className="sb__sub" key={d.driverId}>
            <th scope="row" className="sb__sticky sb__namecol">
              <span className="sb__no">{d.carNumber}</span>
              <button type="button" className="linkish" onClick={() => onDriver(d.driverId)}>
                {d.name}
              </button>
              <span className="sb__shop">{ordinal(d.rank)} in points</span>
            </th>
            {TEAM_COLUMNS.slice(1).map((c) => (
              <td key={c.key} className={c.num ? 'num' : undefined}>
                {subValue(c.key, d)}
              </td>
            ))}
          </tr>
        ))}
    </>
  );
}

/** A driver's answer to the same question the shop's column asked. */
function subValue(key: string, d: StatRow): ReactNode {
  switch (key) {
    case 'points':
      return d.points;
    case 'cars':
      return '';
    case 'starts':
      return d.starts;
    case 'wins':
      return count(d.wins);
    case 'poles':
      return count(d.poles);
    case 'top5':
      return count(d.top5);
    case 'top10':
      return count(d.top10);
    case 'turnsLed':
      return count(d.turnsLed);
    case 'racesLed':
      return count(d.racesLed);
    case 'dnqs':
      return count(d.dnqs);
    case 'dnfs':
      return count(d.dnfs);
    case 'avgStart':
      return one(d.avgStart);
    case 'avgFinish':
      return one(d.avgFinish);
    case 'best':
      return num(d.bestFinish);
    case 'winPct':
      return pctText(d.winPct);
    case 'finPct':
      return pctText(d.finishPct);
    case 'escPct':
      return pctText(d.escapePct);
    case 'heat':
      return count(d.heatIncidents);
    case 'ppst':
      return one(d.avgPointsPerStart);
    case 'winnings':
      return money(d.winningsMb);
    case 'seasons':
      return d.seasons;
    case 'delta':
      return d.rankDelta === null || d.rankDelta === 0 ? '' : d.rankDelta > 0 ? `▲${d.rankDelta}` : `▼${Math.abs(d.rankDelta)}`;
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// The season grid
// ---------------------------------------------------------------------------

/** Which band a finishing position falls in. The grid is read by colour first. */
function pipClass(finish: number): string {
  if (finish === 1) return 'is-win';
  if (finish <= 5) return 'is-t5';
  if (finish <= 10) return 'is-t10';
  if (finish <= 20) return 'is-mid';
  return 'is-back';
}

function cellClass(line: DriverRaceLine | undefined): string {
  if (!line) return 'is-none';
  if (line.outcome === 'dnq') return 'is-dnq';
  if (line.outcome === 'missed-the-show') return 'is-bubble';
  if (line.outcome === 'absent') return 'is-absent';
  if (!line.running) return 'is-out';
  return pipClass(line.finish ?? 43);
}

function cellText(line: DriverRaceLine | undefined): string {
  if (!line) return '';
  if (line.outcome === 'dnq') return 'DNQ';
  if (line.outcome === 'missed-the-show') return 'BUB';
  if (line.outcome === 'absent') return '—';
  return String(line.finish ?? '');
}

function cellTitle(line: DriverRaceLine | undefined, race: RaceColumn, name: string): string {
  if (!line) return `${name} — no record for ${race.raceName}`;
  const head = `${name} · ${race.raceName} (race ${race.raceNumber})`;
  if (line.outcome === 'dnq') return `${head}\nFailed to qualify.`;
  if (line.outcome === 'missed-the-show') return `${head}\nLeft on the bubble — did not race.`;
  if (line.outcome === 'absent') return `${head}\nNot at the track — ${line.note ?? 'absent'}.`;
  const parts = [
    `Started ${line.start ?? '?'}, finished ${line.finish ?? '?'}`,
    line.running ? '' : `OUT — ${line.outReason ?? 'unknown'}`,
    line.turnsLed > 0 ? `led ${line.turnsLed} turn${line.turnsLed === 1 ? '' : 's'}${line.ledMost ? ' (most)' : ''}` : '',
    line.forfeited ? 'points forfeited (TV EVENT X 13)' : `${line.points} point${line.points === 1 ? '' : 's'}`,
    line.winningsMb > 0 ? `won ${formatDollars(line.winningsMb)}` : '',
    line.rankAfter ? `${ordinal(line.rankAfter)} in points after` : '',
  ].filter(Boolean);
  return `${head}\n${parts.join(' · ')}`;
}

function RaceGrid({
  board,
  rows,
  onDriver,
}: {
  board: StatBoard;
  rows: readonly StatRow[];
  onDriver: (id: string) => void;
}) {
  const run = board.races.filter((r) => r.run);
  const shown = run.length > 0 ? board.races : [];
  if (shown.length === 0 || rows.length === 0) {
    return <p className="sb__nothing">The grid fills in with the first checkered flag.</p>;
  }
  return (
    <div className="sb__grid">
      <table className="sb__gridtable">
        <thead>
          <tr>
            <th className="sb__sticky sb__gridname" scope="col">
              Driver
            </th>
            {shown.map((race) => (
              <th
                key={race.raceNumber}
                scope="col"
                className={`sb__gridhead${race.run ? '' : ' is-future'}${race.doublePoints ? ' is-double' : ''}`}
                title={`${race.raceName}\n${race.trackName}${race.location ? ` · ${race.location}` : ''}\n${race.category}${race.doublePoints ? ' · double points' : ''}${
                  race.run ? `\nWon by ${race.winnerName}. Pole: ${race.poleName}.` : '\nNot yet run.'
                }`}
              >
                <span className="sb__gridcrest">
                  <RaceLogo raceKey={race.raceKey} size={26} />
                </span>
                <span className="sb__gridno">{race.raceNumber}</span>
                <span className="sb__gridtrack">{shortName(race.trackName)}</span>
                {race.doublePoints && <span className="sb__gridx2">×2</span>}
              </th>
            ))}
            <th className="num sb__gridtot" scope="col">
              Pts
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.driverId}>
              <th scope="row" className="sb__sticky sb__gridname">
                <span className="sb__rk">{r.rank}</span>
                <TeamMark teamName={r.teamName} size={16} />
                <button type="button" className="linkish" onClick={() => onDriver(r.driverId)}>
                  {r.name}
                </button>
              </th>
              {shown.map((race) => {
                const line = r.byRace[race.raceNumber];
                return (
                  <td
                    key={race.raceNumber}
                    className={`sb__cell ${race.run ? cellClass(line) : 'is-future'}`}
                    title={race.run ? cellTitle(line, race, r.name) : `${race.raceName} — not yet run`}
                  >
                    {race.run ? cellText(line) : ''}
                  </td>
                );
              })}
              <td className="num sb__gridtot">{r.points}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Legend />
    </div>
  );
}

function YearGrid({
  board,
  rows,
  onDriver,
}: {
  board: StatBoard;
  rows: readonly StatRow[];
  onDriver: (id: string) => void;
}) {
  const seasons = board.seasonNumbers;
  if (seasons.length === 0 || rows.length === 0) {
    return <p className="sb__nothing">Nothing to compare yet.</p>;
  }
  return (
    <div className="sb__grid">
      <table className="sb__gridtable">
        <thead>
          <tr>
            <th className="sb__sticky sb__gridname" scope="col">
              Driver
            </th>
            {seasons.map((s) => (
              <th key={s} scope="col" className="sb__gridhead sb__gridhead--year">
                <span className="sb__gridno">S{s}</span>
              </th>
            ))}
            <th className="num sb__gridtot" scope="col">
              Pts
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const lines = board.perSeason[r.driverId] ?? [];
            const byYear = new Map(lines.map((l) => [l.seasonNumber, l]));
            return (
              <tr key={r.driverId}>
                <th scope="row" className="sb__sticky sb__gridname">
                  <span className="sb__rk">{r.rank}</span>
                  <TeamMark teamName={r.teamName} size={16} />
                  <button type="button" className="linkish" onClick={() => onDriver(r.driverId)}>
                    {r.name}
                  </button>
                </th>
                {seasons.map((s) => {
                  const line = byYear.get(s);
                  return (
                    <td
                      key={s}
                      className={`sb__cell ${
                        !line ? 'is-none' : line.champion ? 'is-win' : pipClass(Math.min(line.rank, 43))
                      }`}
                      title={
                        line
                          ? `${r.name} · season ${s}\n${ordinal(line.rank)} in points · ${line.points} pts · ${line.wins} win${
                              line.wins === 1 ? '' : 's'
                            } · ${line.starts} starts · avg finish ${line.avgFinish?.toFixed(1) ?? '—'} · ${formatDollars(
                              line.winningsMb,
                            )}${line.champion ? '\nCUP CHAMPION' : ''}`
                          : `${r.name} did not race in season ${s}`
                      }
                    >
                      {line ? (line.champion ? '★' : line.rank) : ''}
                    </td>
                  );
                })}
                <td className="num sb__gridtot">{r.points}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="dim small">
        Each cell is where he finished that year's championship. ★ is the Cup.
      </p>
    </div>
  );
}

function Legend() {
  return (
    <ul className="sb__legend">
      <li>
        <i className="sb__cell is-win">1</i> win
      </li>
      <li>
        <i className="sb__cell is-t5">5</i> top 5
      </li>
      <li>
        <i className="sb__cell is-t10">9</i> top 10
      </li>
      <li>
        <i className="sb__cell is-mid">17</i> 11–20
      </li>
      <li>
        <i className="sb__cell is-back">31</i> 21 back
      </li>
      <li>
        <i className="sb__cell is-out">38</i> did not finish
      </li>
      <li>
        <i className="sb__cell is-dnq">DNQ</i> failed to qualify
      </li>
      <li>
        <i className="sb__cell is-absent">—</i> not at the track
      </li>
      <li>
        <span className="sb__gridx2">×2</span> double points<Cite> (D-023)</Cite>
      </li>
    </ul>
  );
}

/**
 * "2000s WATKINS GLEN INTERNATIONAL" → "WATKINS GLEN". Enough to read at 46px.
 *
 * ⚠ THE ERA PREFIX HAS TO GO FIRST. Every name on a published track card is
 * stamped with the era the card is from — `2000s TALLADEGA SUPERSPEEDWAY`,
 * `2014 BRISTOL MOTOR SPEEDWAY` — and a naive twelve-character truncation
 * yields "2000s TALLAD" for one track and "2014 BRISTOL" for the next, which is
 * a column of years rather than a column of racetracks. The era is still in the
 * tooltip, where a header has room to say it.
 */
export function shortName(track: string): string {
  return track
    .replace(/^\s*(19|20)\d{2}s?\s+/, '')
    .replace(/\b(Motor\s+)?(Speedway|Raceway|International|Superspeedway|Motorsports?|Park|Complex)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 13);
}
